import { alkymetricsClient } from '../alkymetricsClient';

/**
 * Cookie Consent Service
 * Provides API methods for managing user cookie consent preferences
 * Implements GDPR/CCPA compliant cookie consent management
 */

/**
 * Get current cookie consent preferences for the authenticated partner
 * @returns {Promise<Object|null>} The current consent or null if not found
 */
export const getCurrentConsent = async () => {
  try {
    const { data } = await alkymetricsClient.get('/partner/cookie-consent');
    return data;
  } catch (error) {
    // Return null if no consent found (404), as per API specification
    if (error.response && error.response.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Create or update cookie consent preferences
 * @param {Object} consentData - The consent preferences
 * @param {boolean} consentData.functional - Functional cookies acceptance
 * @param {boolean} consentData.analytics - Analytics cookies acceptance
 * @param {boolean} consentData.marketing - Marketing cookies acceptance
 * @param {string} consentData.consentVersion - Version of the cookie policy
 * @param {string} [consentData.consentMethod='banner'] - Method through which consent was obtained
 * @param {string} [consentData.ipAddress] - IP address of the user (for audit trail)
 * @param {string} [consentData.userAgent] - User agent string (for audit trail)
 * @param {string} [consentData.language] - Language in which consent was presented
 * @returns {Promise<Object>} The created/updated consent
 */
export const upsertConsent = async (consentData) => {
  const { data } = await alkymetricsClient.post('/partner/cookie-consent', consentData);
  return data;
};

/**
 * Revoke cookie consent (soft delete)
 * After revocation, only strictly necessary cookies should be used
 * @returns {Promise<void>}
 */
export const revokeConsent = async () => {
  await alkymetricsClient.delete('/partner/cookie-consent');
};

/**
 * Check if consent renewal is needed
 * Returns true if consent is expired, missing, or policy version has changed
 * @param {string} policyVersion - Current cookie policy version to check against
 * @returns {Promise<Object>} Object with needsRenewal boolean property
 */
export const needsConsentRenewal = async (policyVersion) => {
  const { data } = await alkymetricsClient.get('/partner/cookie-consent/needs-renewal', {
    params: { policyVersion },
  });
  return data;
};

/**
 * Get consent history (for audit purposes)
 * Returns the complete consent history, including revoked consents
 * @returns {Promise<Array>} Array of all consent records
 */
export const getConsentHistory = async () => {
  const { data } = await alkymetricsClient.get('/partner/cookie-consent/history');
  return data;
};

/**
 * Check if a specific cookie category is allowed
 * Returns false if consent is expired or not given
 * @param {string} category - Cookie category to check ('strictlyNecessary' | 'functional' | 'analytics' | 'marketing')
 * @returns {Promise<Object>} Object with allowed boolean property
 */
export const isCategoryAllowed = async (category) => {
  const { data } = await alkymetricsClient.get('/partner/cookie-consent/check-category', {
    params: { category },
  });
  return data;
};
